'use client';

import {offset, useFloating, FloatingPortal, size} from '@floating-ui/react';
import {Combobox, Transition} from '@headlessui/react';
import {useCatchOutsidedClick, useDebouncedValue} from '@rocket-ui/react-tools';
import {noop} from '@rocket-ui/tools';
import cn from 'classnames';
import React, {Fragment, ReactNode, RefObject, useDeferredValue, useState} from 'react';
import {AutocompleteInputOption} from '../types';
import {Loader} from '../AutocompleteLoader';
import {ScrollbarContainer} from '../../ScrollbarContainer';

export interface FloatingAutocompletedInputProps<T> {
	value?: T;
	onChange?: (value: T) => void;
	onBlur?: VoidFunction;
	onFocus?: VoidFunction;
	onCustomCreate?: (value: string) => void;
	onClickOutside?: VoidFunction;
	provider: (query: string) => {data?: T[]; isLoading: boolean};
	placeholder?: string;
	noSearchResultsPlaceholder?: ReactNode | ((props: {query: string}) => ReactNode);
	readKey?: (value?: T) => string;
	readLabel?: (value?: T) => string;
	renderOption?: (props: AutocompleteInputOption<T>) => ReactNode;
	optionDetails?: (props: AutocompleteInputOption<T>) => ReactNode;
	disableOption?: (value: T) => boolean;
	disabled?: boolean;
	matchOptionsBy?: keyof T;
	footer?: ReactNode;
	autoFocus?: boolean;
}

export function FloatingAutocompletedInput<T>({
	value,
	onChange,
	placeholder,
	noSearchResultsPlaceholder,
	provider,
	readKey = (value) => String(value),
	readLabel = (value) => String(value),
	renderOption = ({option}) => <>{String(option)}</>,
	optionDetails,
	disableOption = () => false,
	onBlur = noop,
	onFocus = noop,
	onClickOutside = noop,
	disabled,
	matchOptionsBy,
	footer,
	onCustomCreate = noop,
	autoFocus,
}: FloatingAutocompletedInputProps<T>) {
	const [inFocus, setInFocus] = useState(false);
	const [query, setQuery] = useState('');
	const debouncedQuery = useDebouncedValue(query, 300);
	const deferredQuery = useDeferredValue(debouncedQuery);
	const {data: options = [], isLoading} = provider(deferredQuery);

	const {x, y, strategy, refs} = useFloating({
		placement: 'bottom-start',
		middleware: [
			offset({mainAxis: 12, crossAxis: -56}),
			size({
				apply({rects, elements}) {
					elements.floating.style.width = `${rects.reference.width + 74}px`;
				},
			}),
		],
	});

	useCatchOutsidedClick({
		ref: refs.reference as RefObject<HTMLElement>,
		onCatch: onClickOutside,
	});

	const handleOnFocus = () => {
		setInFocus(true);
		onFocus();
	};

	const handleOnBlur = () => {
		setInFocus(false);
		onBlur();
	};

	const handleInputKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && options.length === 0) onCustomCreate(query);
	};

	const hasFinishedSearchingWithNoResult = !isLoading && options.length === 0;

	return (
		<Combobox value={value ?? null} onChange={onChange} by={matchOptionsBy as any} nullable disabled={disabled}>
			<div className={'relative w-full h-full'} ref={refs.setReference}>
				<div className={cn('relative grid place-items-center w-full h-full rounded-md', 'ym-hide-content')}>
					<Combobox.Input
						className={
							'w-full h-full px-4 bg-transparent border-none outline-none rounded-md text-sm placeholder:text-black/30'
						}
						onChange={(event) => setQuery(event.target.value)}
						onKeyDown={handleInputKeyDown}
						displayValue={(value) => (value ? readLabel(value as any) : '')}
						placeholder={placeholder}
						onFocus={handleOnFocus}
						onBlur={handleOnBlur}
						autoCorrect={'off'}
						autoComplete={'off'}
						autoFocus={autoFocus}
						ref={refs.setReference}
					/>

					{!inFocus && value && optionDetails ? (
						<div className={'absolute inset-0 flex w-full pointer-events-none px-4'}>
							<span className={'flex-1 flex items-center gap-2 overflow-hidden truncate'}>
								<span className={'flex-none text-sm opacity-0'}>{readLabel(value)}</span>
								{optionDetails({
									option: value,
									active: false,
									selected: true,
									disabled: false,
								})}
							</span>
						</div>
					) : null}
				</div>
				<FloatingPortal>
					<Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
						<Combobox.Options
							className={
								'absolute inset-x-0 z-10 backdrop-blur-3xl bg-black/[.04] shadow-md rounded-md border border-black/[.04]'
							}
							ref={refs.setFloating}
							style={{
								position: strategy,
								left: `${x}px`,
								top: `${y}px`,
							}}
						>
							{hasFinishedSearchingWithNoResult ? (
								<div className={'p-5 text-black/30'}>
									{typeof noSearchResultsPlaceholder === 'function'
										? noSearchResultsPlaceholder({query})
										: noSearchResultsPlaceholder}
								</div>
							) : isLoading ? (
								<div className={'p-5'}>
									<Loader />
								</div>
							) : (
								<ScrollbarContainer className={'max-h-44 p-2.5'}>
									{options.map((option) => {
										const disabled = disableOption(option);

										return (
											<Combobox.Option
												key={readKey(option)}
												value={option}
												as={'div'}
												disabled={disabled}
												className={({active}) =>
													cn('px-2.5', {
														'bg-black/[.02]': active,
														'cursor-pointer': !disabled,
														'text-black/30': disabled,
													})
												}
											>
												{({active, selected}) => (
													<div className={'flex items-center gap-2 w-full h-full'}>
														{selected ? <div className={'flex-none w-1 h-1 bg-black rounded-full'} /> : null}
														{renderOption({option, selected, active, disabled})}
													</div>
												)}
											</Combobox.Option>
										);
									})}
								</ScrollbarContainer>
							)}
							{footer ? <div className={'w-full px-2.5 border-t border-black/[.08]'}>{footer}</div> : null}
						</Combobox.Options>
					</Transition>
				</FloatingPortal>
			</div>
		</Combobox>
	);
}
